import { createTheme, Theme } from '@mui/material/styles';
import * as t from './tokens';

export type ColorMode = 'light' | 'dark';

// ─── Custom palette keys ─────────────────────────────────────────────────────
// `accent` is the safe icon/highlight color for the current mode (gold on dark
// surfaces, purple on light). `gradients` carries the brand gradients that MUI
// can't express on palette.primary.main directly (must go through backgroundImage).
declare module '@mui/material/styles' {
    interface Palette {
        accent: Palette['primary'];
        gradients: {
            page: string;
            card: string;
            button: string;
            buttonHover: string;
        };
        // Decorative layers for the stacked badge-card look (legacy .badgeCard_LP).
        stack: {
            near: string;
            far: string;
        };
    }
    interface PaletteOptions {
        accent?: PaletteOptions['primary'];
        gradients?: {
            page: string;
            card: string;
            button: string;
            buttonHover: string;
        };
        stack?: {
            near: string;
            far: string;
        };
    }
}

/**
 * Build the MUI theme for a given color mode.
 *
 * Dark (default) is a faithful match of the legacy .NET app: purple-gradient
 * page, white text, gold-gradient buttons, yellow links. Light is a genuine
 * light alternative: off-white page, white cards, dark text — brand accents in
 * purple/gold, buttons keep the gold gradient. The AppBar stays deep-purple
 * chrome in both modes for brand consistency.
 */
export function makeTheme(mode: ColorMode): Theme {
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: t.PURPLE_PRIMARY,
                dark: t.PURPLE_PRIMARY_DARK,
                light: t.PURPLE_PRIMARY_LIGHT,
                contrastText: t.WHITE,
            },
            secondary: {
                main: t.GOLD,
                dark: t.GOLD_DARK,
                light: t.GOLD_LIGHT,
                contrastText: t.WHITE,
            },
            // Icon/highlight accent that reads correctly on the mode's surfaces.
            accent: {
                main: isDark ? t.GOLD : t.PURPLE_PRIMARY,
                contrastText: t.WHITE,
            },
            gradients: {
                page: isDark ? t.PAGE_GRADIENT_DARK : 'none',
                card: isDark ? t.CARD_GRADIENT : 'none',
                button: t.BUTTON_GRADIENT,
                buttonHover: t.BUTTON_GRADIENT_HOVER,
            },
            stack: {
                near: isDark ? t.STACK_NEAR : t.STACK_NEAR_LIGHT,
                far: isDark ? t.STACK_FAR : t.STACK_FAR_LIGHT,
            },
            background: {
                default: isDark ? t.PURPLE_BG_END : t.LIGHT_BG,
                paper: isDark ? t.PURPLE_CARD_END : t.WHITE,
            },
            text: {
                primary: isDark ? t.WHITE : t.TEXT_DARK,
                secondary: isDark ? 'rgba(255,255,255,0.7)' : t.TEXT_MUTED_DARK,
                disabled: isDark ? 'rgba(255,255,255,0.4)' : '#999999',
            },
            divider: isDark ? t.DIVIDER_DARK : t.DIVIDER_LIGHT,
            success: { main: t.SUCCESS_GREEN, dark: t.SUCCESS_GREEN_DARK },
            error: { main: t.ERROR_RED },
            warning: { main: t.WARNING_ORANGE },
        },
        typography: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: isDark
                        ? {
                              backgroundImage: t.PAGE_GRADIENT_DARK,
                              backgroundAttachment: 'fixed',
                              backgroundColor: t.PURPLE_BG_END,
                              color: t.WHITE,
                              minHeight: '100vh',
                          }
                        : {
                              backgroundColor: t.LIGHT_BG,
                              color: t.TEXT_DARK,
                              minHeight: '100vh',
                          },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    // Let dark-surface containers cascade their text colour.
                    root: { color: 'inherit' },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: isDark
                        ? {
                              backgroundImage: t.CARD_GRADIENT,
                              color: t.WHITE,
                              border: `1px solid ${t.PURPLE_BORDER}`,
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                              '& .MuiTypography-root': { color: 'inherit' },
                          }
                        : {
                              backgroundColor: t.WHITE,
                              color: t.TEXT_DARK,
                              border: `1px solid ${t.DIVIDER_LIGHT}`,
                              boxShadow: '0 2px 10px rgba(26, 15, 79, 0.08)',
                          },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                },
            },
            MuiAppBar: {
                // Deep-purple chrome in both modes (matches legacy navbar).
                styleOverrides: {
                    root: {
                        backgroundColor: t.PURPLE_DEEP,
                        color: t.WHITE,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    variant: 'contained',
                    color: 'primary',
                    size: 'small',
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none' as const,
                        fontWeight: 600,
                        borderRadius: 4,
                    },
                    // Primary = gold gradient in BOTH modes. MUI's default hover
                    // sets backgroundColor, which can't override a backgroundImage,
                    // so the gradient is re-asserted on hover.
                    containedPrimary: {
                        backgroundImage: t.BUTTON_GRADIENT,
                        backgroundColor: t.GOLD,
                        color: t.WHITE,
                        '&:hover': {
                            backgroundImage: t.BUTTON_GRADIENT,
                            filter: 'brightness(1.06)',
                        },
                    },
                    // Cancel / Back convention: solid purple, white text.
                    containedSecondary: {
                        backgroundImage: 'none',
                        backgroundColor: t.PURPLE_BORDER,
                        color: t.WHITE,
                        '&:hover': { backgroundColor: t.PURPLE_PRIMARY },
                    },
                    outlinedPrimary: {
                        borderColor: t.GOLD,
                        color: isDark ? t.GOLD : t.GOLD_DARK,
                        '&:hover': {
                            borderColor: t.GOLD_DARK,
                            backgroundColor: 'rgba(245, 166, 35, 0.08)',
                        },
                    },
                },
            },
            MuiButtonGroup: {
                defaultProps: {
                    variant: 'contained',
                    color: 'primary',
                    size: 'small',
                    disableElevation: true,
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        color: isDark ? t.LINK_YELLOW : t.PURPLE_PRIMARY,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    },
                },
            },
            // Inputs stay light/white in both modes (legacy LoginPage does the same;
            // readable on the dark page).
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: t.WHITE,
                        color: '#333333',
                        borderRadius: 4,
                        '& fieldset': { borderColor: '#CCCCCC' },
                        '&:hover fieldset': { borderColor: '#999999' },
                        '&.Mui-focused fieldset': { borderColor: t.GOLD },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: '#666666',
                        '&.Mui-focused': { color: t.GOLD_DARK },
                    },
                    shrink: {
                        backgroundColor: t.WHITE,
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        borderRadius: '2px',
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    icon: { color: '#666666' },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: isDark
                        ? {
                              backgroundColor: t.PURPLE_PRIMARY_DARK,
                              '& .MuiTableCell-head': { color: t.WHITE },
                          }
                        : {
                              backgroundColor: '#F0EEF5',
                              '& .MuiTableCell-head': { color: t.TEXT_DARK, fontWeight: 700 },
                          },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: isDark
                        ? {
                              backgroundImage: t.CARD_GRADIENT,
                              backgroundColor: t.PURPLE_PRIMARY_DARK,
                              color: t.WHITE,
                              border: `1px solid ${t.PURPLE_BORDER}`,
                              '& .MuiTypography-root': { color: 'inherit' },
                          }
                        : {
                              backgroundColor: t.WHITE,
                              color: t.TEXT_DARK,
                              border: `1px solid ${t.DIVIDER_LIGHT}`,
                          },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: isDark
                        ? {
                              backgroundColor: t.PURPLE_PRIMARY_DARK,
                              color: t.WHITE,
                              borderColor: t.PURPLE_BORDER,
                              '& .MuiTypography-root': { color: 'inherit' },
                          }
                        : {
                              backgroundColor: t.WHITE,
                              color: t.TEXT_DARK,
                              borderColor: t.DIVIDER_LIGHT,
                          },
                },
            },
        },
    });
}

// Back-compat default export for any module importing the old static theme.
// The app's real theme is provided per-mode by ColorModeProvider.
export const theme = makeTheme('dark');
